import AuthLayout from '@/layouts/AuthLayout/AuthLayout.vue'
import MainLayout from '@/layouts/MainLayout/MainLayout.vue'
import StartScreenLayout from '@/layouts/StartScreenLayout/StartScreenLayout.vue'
import ApiDocsLayout from '@/layouts/ApiDocsLayout/ApiDocsLayout.vue'
import { useAuthStore } from '@/store/auth/auth'
import { computed, defineComponent, onMounted, onUnmounted } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import type {
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric,
  NavigationGuardNext,
} from 'vue-router'
import { useMainStore } from '@/store/main/main'
import { scrollIntoView } from '@/helpers/scroll'
import { useUIStore } from '../store/ui/ui'

export default defineComponent({
  name: 'App',
  components: {
    RouterView,
    AuthLayout,
    MainLayout,
    StartScreenLayout,
    ApiDocsLayout,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const mainStore = useMainStore()
    const authStore = useAuthStore()
    const uiStore = useUIStore()

    const layoutComponent = computed(() => `${route.meta.layout}Layout`)
    const componentKey = computed(() => {
      return route.path
    })

    const ALLOWED_PAGES_WITHOUT_AUTH = [
      'login',
      'recovery',
      'update',
      'registry',
      'adminLogin',
      'download',
    ]

    const isPublicRoute = (routeName: string) => ALLOWED_PAGES_WITHOUT_AUTH.includes(routeName)
    const isInitialStepsRoutes = (routeName: string) =>
      ['download', 'startScreen'].includes(routeName)
    const hasCompletedSteps = computed(() => authStore.user?.step === 'done')

    const handleAuthGuard = (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedLoadedGeneric,
      next: NavigationGuardNext,
    ) => {
      const routeName = String(to.name)
      const isAuthorized = authStore.isAuth
      const toAdminRoute = to.meta.requiresAdmin

      if (isAuthorized && isPublicRoute(routeName) && routeName !== 'download') {
        return next({ path: from.path, replace: true })
      }

      if (!isAuthorized && !isPublicRoute(routeName)) {
        return next({ name: 'login', replace: true })
      }

      if (authStore.isAdmin && !toAdminRoute) {
        return next({ name: 'adminCompanies', replace: true })
      }

      if (!authStore.isAdmin) {
        if (toAdminRoute) {
          return next({ name: 'home', replace: true })
        }
        if (isAuthorized && !isInitialStepsRoutes(to.name as string) && !hasCompletedSteps.value) {
          return next({
            name: 'startScreen',
            replace: true,
            query: { step: authStore.user?.step || '1' },
          })
        }
        if (to.name === 'startScreen' && hasCompletedSteps.value) {
          return next({ path: from.path, replace: true })
        }
      }

      return next()
    }

    const handleRouting = (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedLoadedGeneric,
      next: NavigationGuardNext,
    ) => {
      scrollIntoView(0, 'instant')

      if (route.name === 'download') {
        return next()
      }

      return handleAuthGuard(to, from, next)
    }

    router.beforeEach(handleRouting)

    const setAppWindowWidth = () => {
      mainStore.$patch({
        windowWidth: window.innerWidth,
      })
    }

    onMounted(async () => {
      window.addEventListener('resize', setAppWindowWidth)

      await router.isReady()
      const routeName = String(route.name)

      if (isInitialStepsRoutes(routeName)) {
        return
      }
      if (mainStore.isMobile) {
        uiStore.isMenuOpen = false
        await router.push('/builder')
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', setAppWindowWidth)
    })

    return { layoutComponent, componentKey }
  },
})
